import { createFevent, updateFevent } from '@shared/api/fevent/fevent.controller';
import {
  FeventCreate,
  FeventOne,
  FeventStatus,
  FeventUpdate,
} from '@shared/api/fevent/fevent.interface';
import { updateMember } from '@shared/api/member/member.controller';
import { User } from '@shared/api/user/user.interface';
import { notificateError } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { LowerRole, UserRole } from '@shared/types';
import { getReverseType } from '@shared/utils/memberUtils';
import { NotificationInstance } from 'antd/es/notification/interface';
import _ from 'lodash';

import {
  DeceasedInfoFormInputsEnum,
  DeceasedInfoIFormInputs,
} from '../interfaces/feventAddEdit.interface';

export enum FeventFormUseMode {
  Edit = 'edit',
  Send = 'send',
  Add = 'add',
}

//* 생성
export const onFeventDeceasedInfoSubmit = async (
  user: User,
  data: DeceasedInfoIFormInputs,
  setSearchParams: any,
  bugoRole: LowerRole,
  notifiacationInstance: NotificationInstance | null,
) => {
  if (
    _.isNil(data[DeceasedInfoFormInputsEnum.Name]) ||
    data[DeceasedInfoFormInputsEnum.Name].length === 0
  ) {
    notificateError(notifiacationInstance, '고인성함을 작성해주세요');
  }
  const feventCreate: FeventCreate = {
    user: user._id,
    bugoBrand: user.roles.includes(UserRole.BugoAgencyAdmin)
      ? user.bugoAgencyAdminDetail?.bugoBrands[0]?._id ?? 'agencyAdminHasNoBugoBrand'
      : user.bugoBrand,
    bugoBrandAgencyIndex: data[DeceasedInfoFormInputsEnum.BugoBrandAgencyIndex],
    registerNumber: data[DeceasedInfoFormInputsEnum.RegisterNumber],
    funeralHomeInfo: data[DeceasedInfoFormInputsEnum.FuneralHomeInfo],
    funeralHomeInfoEmbed: data[DeceasedInfoFormInputsEnum.FuneralHomeInfoEmbed],
    roomInfo: data[DeceasedInfoFormInputsEnum.RoomInfo]
      ? data[DeceasedInfoFormInputsEnum.RoomInfo]
      : undefined,
    roomInfoEmbed: data[DeceasedInfoFormInputsEnum.RoomInfoEmbedName]
      ? {
          name: data[DeceasedInfoFormInputsEnum.RoomInfoEmbedName],
        }
      : undefined,
    members: undefined,
    shopOrders: undefined,
    memberOrderList: [],
    mournerText: data[DeceasedInfoFormInputsEnum.MournerText],
    deceasedInfo: {
      name: data[DeceasedInfoFormInputsEnum.Name],
      age: data[DeceasedInfoFormInputsEnum.Age],
      sex: data[DeceasedInfoFormInputsEnum.Sex],
      cemetery: data[DeceasedInfoFormInputsEnum.Cemetery],
      coffinIn: data[DeceasedInfoFormInputsEnum.CoffinInDate],
      coffinOut: data[DeceasedInfoFormInputsEnum.CoffinOutDate],
      death: data[DeceasedInfoFormInputsEnum.DeathDate],
      enterance: data[DeceasedInfoFormInputsEnum.EnteranceDate],
      bugoTerm: data[DeceasedInfoFormInputsEnum.BugoTerm],
      dpImage: data[DeceasedInfoFormInputsEnum.DpImage],
    },
    status: FeventStatus.Doing,
    useAcqMsg: data[DeceasedInfoFormInputsEnum.UseAcqMsg] ?? false,
  };

  const feventCreated = await createFevent(feventCreate, bugoRole);
  setSearchParams({ feventId: feventCreated._id, step: 1 });
};

//* 수정
export const onFeventDeceasedInfoUpdate = async (
  feventId: string,
  data: DeceasedInfoIFormInputs,
  setSearchParams: any,
  bugoRole: LowerRole,
  notifiacationInstance: NotificationInstance | null,
  editMode?: boolean,
  fevent?: FeventOne,
) => {
  if (
    _.isNil(data[DeceasedInfoFormInputsEnum.Name]) ||
    data[DeceasedInfoFormInputsEnum.Name].length === 0
  ) {
    notificateError(notifiacationInstance, '고인성함을 작성해주세요');
  }
  const feventUpdate: FeventUpdate = {
    _id: feventId,
    bugoBrandAgencyIndex: data[DeceasedInfoFormInputsEnum.BugoBrandAgencyIndex],
    registerNumber: data[DeceasedInfoFormInputsEnum.RegisterNumber],
    funeralHomeInfo: data[DeceasedInfoFormInputsEnum.FuneralHomeInfo],
    funeralHomeInfoEmbed: data[DeceasedInfoFormInputsEnum.FuneralHomeInfoEmbed],
    roomInfo: data[DeceasedInfoFormInputsEnum.RoomInfo]
      ? data[DeceasedInfoFormInputsEnum.RoomInfo]
      : undefined,
    roomInfoEmbed: data[DeceasedInfoFormInputsEnum.RoomInfoEmbedName]
      ? {
          name: data[DeceasedInfoFormInputsEnum.RoomInfoEmbedName],
        }
      : undefined,
    mournerText: data[DeceasedInfoFormInputsEnum.MournerText],
    deceasedInfo: {
      name: data[DeceasedInfoFormInputsEnum.Name],
      age: data[DeceasedInfoFormInputsEnum.Age],
      sex: data[DeceasedInfoFormInputsEnum.Sex],
      cemetery: data[DeceasedInfoFormInputsEnum.Cemetery],
      coffinIn: data[DeceasedInfoFormInputsEnum.CoffinInDate],
      coffinOut: data[DeceasedInfoFormInputsEnum.CoffinOutDate],
      enterance: data[DeceasedInfoFormInputsEnum.EnteranceDate],
      death: data[DeceasedInfoFormInputsEnum.DeathDate],
      bugoTerm: data[DeceasedInfoFormInputsEnum.BugoTerm],
      dpImage: data[DeceasedInfoFormInputsEnum.DpImage],
    },
    status: FeventStatus.Doing,
    useAcqMsg: data[DeceasedInfoFormInputsEnum.UseAcqMsg] ?? false,
  };
  await updateFevent(feventUpdate, bugoRole);

  //* 성별 변경 시 기존 member들의 reverseType 일괄 업데이트
  const newSex = data[DeceasedInfoFormInputsEnum.Sex];
  const oldSex = fevent?.deceasedInfo?.sex;
  if (fevent && oldSex !== newSex) {
    const members = fevent.memberOrderList;
    if (members && members.length > 0) {
      await Promise.all(
        members.map(async (member) => {
          const newReverseType = getReverseType(member.relationType, newSex);
          if (member.reverseType !== newReverseType) {
            await updateMember(
              { _id: member._id, reverseType: newReverseType },
              bugoRole,
            );
          }
        }),
      );
    }
  }

  // 부고 생성인 경우
  if (!editMode) {
    setSearchParams({ feventId: feventId, step: 1 });
  }
};
